import { api as axios } from '../utils/apiClient';
import { getAuthToken } from '../utils/authStorage';

/**
 * Create Backup (Automatically uploads to Google Drive)
 * API: POST /api/backup-request/:siteId
 * 
 * This triggers:
 * 1. WordPress creates backup files
 * 2. Backend downloads files to local server
 * 3. Backend uploads files to Google Drive (automatic)
 * 4. Backend deletes local files after upload
 * 5. Returns backup metadata with Google Drive links
 * 
 * Based on API_CALL_EXAMPLES.md
 */
export const requestBackup = async (siteId) => {
  console.log('[BackupService] Creating backup for site:', siteId);
  console.log('[BackupService] API: POST /api/backup-request/' + siteId);
  
  const response = await axios.post(`/api/backup-request/${siteId}`, {}, {
    headers: {
      'Content-Type': 'application/json',
    },
    // Don't keep the browser waiting forever, but allow a reasonable time
    // window so fast/normal backups still show a normal success message.
    timeout: 60 * 1000, // 60 seconds
  });
  
  const backupData = response.data?.data || response.data;
  const storageType = backupData.storageType || 'local';
  
  console.log('[BackupService] Backup created successfully');
  console.log('[BackupService] Storage Type:', storageType);
  
  if (storageType === 'google-drive' && backupData.googleDrive) {
    console.log('[BackupService] ✅ Backup uploaded to Google Drive');
    console.log('[BackupService] Google Drive Folder:', backupData.googleDrive.folderId);
    console.log('[BackupService] Files ZIP Link:', backupData.googleDrive.filesZipLink);
    console.log('[BackupService] Database SQL Link:', backupData.googleDrive.dbSqlLink);
  } else {
    console.log('[BackupService] ⚠️  Backup stored locally (Google Drive upload may have failed)');
  }
  
  return response;
};

/**
 * List Backups for a Site
 * API: GET /api/backup-request/:siteId
 * Returns list of backups with download URLs
 * Note: This endpoint automatically triggers cleanup of old backups in the background
 */
export const fetchBackupsForSite = async (siteId) => {
  console.log('[BackupService] Fetching backups for site:', siteId);
  console.log('[BackupService] API: GET /api/backup-request/' + siteId);
  
  const response = await axios.get(`/api/backup-request/${siteId}`);
  
  console.log('[BackupService] Found', response.data?.length || 0, 'backups');
  
  return response;
};

/**
 * Delete Backup
 * API: DELETE /api/backup-request/:siteId/:timestamp
 * Deletes backup files and database record to free up space
 */
export const deleteBackup = async (siteId, timestamp) => {
  console.log('[BackupService] Deleting backup for site:', siteId, 'timestamp:', timestamp);
  console.log('[BackupService] API: DELETE /api/backup-request/' + siteId + '/' + timestamp);
  
  const token = getAuthToken();
  if (!token) {
    console.error('[BackupService] ❌ No auth token found');
    const error = new Error('Authentication required. Please log in again.');
    error.response = { status: 401, data: { message: 'Authentication required. Please log in again.' } };
    throw error;
  }
  
  const requestHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
  
  const response = await axios.delete(`/api/backup-request/${siteId}/${encodeURIComponent(timestamp)}`, {
    headers: requestHeaders,
  });
  
  console.log('[BackupService] ✅ Backup deleted successfully');
  return response;
};

/**
 * Trigger Backup Cleanup
 * API: POST /api/backup-request/cleanup
 * Manually triggers cleanup of backups older than 15 days
 */
export const triggerBackupCleanup = async () => {
  console.log('[BackupService] Triggering backup cleanup...');
  console.log('[BackupService] API: POST /api/backup-request/cleanup');
  
  try {
    const response = await axios.post('/api/backup-request/cleanup', {});
    console.log('[BackupService] ✅ Cleanup triggered successfully');
    return response;
  } catch (error) {
    console.error('[BackupService] ❌ Failed to trigger cleanup:', error);
    throw error;
  }
};

